import { get, reduce, orderBy } from 'lodash-es';
import { isNode } from 'client/utils/environment';
import { EdmundsAPI } from 'client/data/api/api-client';
import { createModelSegment } from 'client/data/luckdragon/segment';
import { withMetrics } from 'client/data/api/api-metrics';
import { CarBuyingStorage } from 'site-modules/car-buying/util/storage';
import { PageModel } from 'client/data/models/page';
import { INCENTIVE_NAMES } from 'client/site-modules/shared/constants/inventory/price-analysis';
import { getDigitalRetailPageState } from 'site-modules/shared/utils/car-buying/page-recognition-utils';
import { STORAGE_KEYS } from 'site-modules/shared/constants/car-buying';
import { HIDE_INTERSTITIAL } from 'site-modules/shared/constants/car-buying/feature-flags';
import { isUsed } from 'site-modules/shared/utils/inventory-utils/is-used';
import { getPhotoConfig, DEFAULT_WIDTH } from 'site-modules/shared/utils/inventory-image-utils';
import { getDefaultApiOptionsFromContext } from 'site-modules/shared/utils/car-buying/api-helper';
import { NON_CAPITALIZED_TAXES_AND_FEES_SUM } from 'site-modules/shared/constants/calculator/calculator';
import { ASPECT } from 'client/utils/inventory-image-helper';

import { InventoryModel } from './inventory';
import { VisitorModel } from './visitor';
import { FeatureFlagModel } from './feature-flag';
import { UserProfileModel } from './profile/profile';

const EMPTY_PATH_VALUE = 'none';

export const MODEL = {
  CALCULATED_OFFER: `vins["{vin}"].${STORAGE_KEYS.CALCULATED_OFFER}`,
  ENGINE_SPECIFICATION: 'vins["{vin}"].engineSpecification',
  DISCLAIMERS: 'vins["{vin}"].digitalRetailDisclaimer',
  DIGITAL_RETAIL_OFFERS: 'vins["{vin}"].digitalRetailOffers',
  MAKES: 'makes',
  PROFILE: `storageData.${STORAGE_KEYS.PROFILE}`,
  TOTAL_MONTHLY_PAYMENT: `vins["{vin}"].${STORAGE_KEYS.TOTAL_MONTHLY_PAYMENT}`,
  VIN: `storageData.${STORAGE_KEYS.VIN}`,
  LAST_VISITED_STEP: 'vins["{vin}"].lastVisitedStep',
  VISITED_TAB: `vins["{vin}"].${STORAGE_KEYS.VISITED_TAB}`,
  CURATED_MAKES: 'curatedMakes["{paymentType}"]',
  CURATED_MODELS: 'curatedModels["{paymentType}"].makes["{makeSlug}"].models',
  OFFERS_TOTAL_NUMBER: 'offersTotalNumber["{paymentType}"]',
  DEALER_MAPPING: 'dealerMapping.franchises["{id}"].vins["{vin}"]',
  RECEIPT_ERROR: 'vins["{vin}"].receiptError',
  HIDE_INTERSTITIAL: `storageData.${STORAGE_KEYS.HIDE_INTERSTITIAL}`,
  HIDE_INTERSTITIAL_PER_SESSION: `vin["{vin}"].${STORAGE_KEYS.HIDE_INTERSTITIAL_PER_SESSION}`,
  USER_INFO: 'userInformation',
  SELECTED_INCENTIVES: `vins["{vin}"].${STORAGE_KEYS.INCENTIVES}`,
  SELECTED_LEASE_INCENTIVES: `vins["{vin}"].${STORAGE_KEYS.INCENTIVES}.lease`,
  SELECTED_LOAN_INCENTIVES: `vins["{vin}"].${STORAGE_KEYS.INCENTIVES}.loan`,
  SELECTED_PRIMARY_LEASE_INCENTIVES: `vins["{vin}"].${STORAGE_KEYS.INCENTIVES}.primaryLease`,
  SELECTED_PRIMARY_LOAN_INCENTIVES: `vins["{vin}"].${STORAGE_KEYS.INCENTIVES}.primaryLoan`,
  CONDITIONAL_LEASE_INCENTIVES: `vins["{vin}"].${STORAGE_KEYS.INCENTIVES}.conditionalLease.all`,
  CONDITIONAL_LEASE_INCENTIVES_ELIGIBLE: `vins["{vin}"].${STORAGE_KEYS.INCENTIVES}.conditionalLease.eligible`,
  CONDITIONAL_LOAN_INCENTIVES: `vins["{vin}"].${STORAGE_KEYS.INCENTIVES}.conditionalLoan.all`,
  PHOTO_URL: 'vins["{vin}"].photoUrl.ratio["{ratio}"].disableML["{disableML}"]',
  DELIGHTED_SURVEY_COMPLETED: 'isDelightedSurveyCompleted',
  IS_OVERLAPPING_DEALER: 'vin["{vin}"].isOverlappingDealer',
  RESET_CUSTOM_VEHICLE_PRICE: 'vin["{vin}"].resetCustomVehiclePrice',
  STYLE_LEASE_TERMS: 'styles["{styleId}"].leaseTerms',
  DELIVERY_FEES_INCLUDED: 'isDeliveryFeesIncluded',
};
export const PATHS = {
  CALCULATED_OFFER: ({ vin }) => `vins["${vin}"].calculatedOffer`,
  CREDIT_RESPONSE: 'creditResponse',
  CURATED_MODELS: ({ make, paymentType }) => `curatedModels["${paymentType}"].makes["${make}"].models`,
  CURATED_MAKES: ({ paymentType }) => `curatedMakes["${paymentType}"]`,
  DEALER_INFO: ({ vin }) => `vin["${vin}"]`,
  DEALER_MAPPING: ({ vin, id = EMPTY_PATH_VALUE }) => `dealerMapping.franchises["${id}"].vins["${vin}"]`,
  DEALER_NAME: ({ vin }) => `vin["${vin}"].dealerInfo.name`,
  DEALER_PHONE_NUMBER: ({ vin }) => `vin["${vin}"].computedDisplayInfo.phoneNumber`,
  DEALER_ZIP_CODE: ({ vin }) => `vin["${vin}"].dealerInfo.address.zip`,
  DISCLAIMER: ({ vin }) => `vins["${vin}"].digitalRetailDisclaimer`,
  RECEIPT_ERROR: ({ vin }) => `vins["${vin}"].receiptError`,
  IS_LOADED: 'loading.isLoaded',
  OFFERS_TOTAL_NUMBER: ({ paymentType }) => `offersTotalNumber["${paymentType}"]`,
  DIGITAL_RETAIL_OFFERS: ({ vin }) => `vins["${vin}"].digitalRetailOffers`,
  LOCATION: 'location',
  PHOTO_URL: ({ vin, ratio = false, disableML = false }) =>
    `vins["${vin}"].photoUrl.ratio["${ratio}"].disableML["${disableML}"]`,
  PROFILE_DATA: 'storageData.profileData',
  PROFILE_EXPIRATION: 'storageData.profileData.expiration',
  PROFILE: 'storageData.profileData.profile',
  ROOFTOP_ID: ({ vin }) => `vin["${vin}"].dealerInfo.rooftopId`,
  HIDE_INTERSTITIAL: MODEL.HIDE_INTERSTITIAL,
  HIDE_INTERSTITIAL_PER_SESSION: ({ vin }) => `vin["${vin}"].hideInterstitialPerSession`,
  STORED_STATE_CODE: 'location.stateCode',
  STORED_ZIP_CODE: 'location.zipCode',
  TOTAL_MONTHLY_PAYMENT: ({ vin }) => `vins["${vin}"].totalMonthlyPayment`,
  VEHICLE: ({ vin }) => `vin["${vin}"]`,
  VERIFIED_PAYMENTS: ({ vin }) => `vins["${vin}"].verifiedPayments`,
  LAST_VISITED_STEP: ({ vin }) => `vins["${vin}"].lastVisitedStep`,
  VISITED_TAB: ({ vin }) => `vins["${vin}"].visitedTab`,
  VEHICLE_TYPE: ({ vin }) => `vin["${vin}"].type`,
  DEAL_TYPE: ({ vin }) => `vin["${vin}"].computedDisplayInfo.priceValidation.dealType`,
  USER_INFO: MODEL.USER_INFO,
  SELECTED_INCENTIVES: ({ vin }) => `vins["${vin}"].${STORAGE_KEYS.INCENTIVES}`,
  SELECTED_LEASE_INCENTIVES: ({ vin }) => `vins["${vin}"].${STORAGE_KEYS.INCENTIVES}.lease`,
  SELECTED_LOAN_INCENTIVES: ({ vin }) => `vins["${vin}"].${STORAGE_KEYS.INCENTIVES}.loan`,
  SELECTED_PRIMARY_LEASE_INCENTIVES: ({ vin }) => `vins["${vin}"].${STORAGE_KEYS.INCENTIVES}.primaryLease`,
  SELECTED_PRIMARY_LOAN_INCENTIVES: ({ vin }) => `vins["${vin}"].${STORAGE_KEYS.INCENTIVES}.primaryLoan`,
  CONDITIONAL_LEASE_INCENTIVES: ({ vin }) => `vins["${vin}"].${STORAGE_KEYS.INCENTIVES}.conditionalLease.all`,
  CONDITIONAL_LEASE_INCENTIVES_ELIGIBLE: ({ vin }) =>
    `vins["${vin}"].${STORAGE_KEYS.INCENTIVES}.conditionalLease.eligible`,
  CONDITIONAL_LOAN_INCENTIVES: ({ vin }) => `vins["${vin}"].${STORAGE_KEYS.INCENTIVES}.conditionalLoan.all`,
  DELIGHTED_SURVEY_COMPLETED: 'isDelightedSurveyCompleted',
  IS_OVERLAPPING_DEALER: ({ vin }) => `vin["${vin}"].isOverlappingDealer`,
  RESET_CUSTOM_VEHICLE_PRICE: ({ vin }) => `vins["${vin}"].resetCustomVehiclePrice`,
  STYLE_LEASE_TERMS: ({ styleId }) => `styles["${styleId}"].leaseTerms`,
  DELIVERY_FEES_INCLUDED: 'isDeliveryFeesIncluded',
};

const ROUTE_ONE_PROVIDER = 'RouteOne';

export function getDisclaimerUrl(vin, calculatedOffer) {
  const { annualMileage, dueAtSigning, term, monthlyPayment, downPayment, paymentWithTaxes } = calculatedOffer;
  const { securityDeposit = 0, nonCapitalizedTaxesAndFees = 0 } = paymentWithTaxes;

  const params = [
    `vin=${vin}`,
    `dueAtSigning=${dueAtSigning || 0}`,
    `mileage=${annualMileage}`,
    `term=${term}`,
    `paymentWithTaxes=${monthlyPayment}`,
    `downPayment=${downPayment}`,
    `securityDeposit=${securityDeposit}`,
    `salesTaxAmount=${0}`,
    `nonCapitalizedTaxes=${0}`,
    `nonCapitalizedFees=${nonCapitalizedTaxesAndFees}`,
  ];

  return `/inventory/v5/offerdisclaimer?${params.join('&')}`;
}

function transformToSlug(key) {
  return String(key).replace(/\s/g, '-').toLowerCase();
}

/**
 * Generates base slug as obj = { slug: 'string', name: 'string'}
 * Adds additional object into base obj by specific fieldName id they exist
 * @param key { string }
 * @param obj { object }
 * @param fieldName { string }
 * @returns {{}}
 */
function generateBaseSlug(key, obj, fieldName) {
  const slug = transformToSlug(key);
  const baseSlug = {
    [slug]: {
      slug,
      name: key,
    },
  };
  if (obj) {
    baseSlug[slug][fieldName] = obj;
  }
  return baseSlug;
}

/**
 * Generate object from response:
 *
 * Response:
 * {
 *   Chevrolet: 666,
 *   Toyota: 664
 * }
 *
 * Result:
 * {
 *   chevrolet: {name: Chevrolet, slug: chevrolet},
 *   toyota: {name: Toyota, slug: toyota}
 * }
 *
 * @param {Object} items
 * @param {Object} obj
 * @param {string} fieldName
 * @returns {Object} items with transformed objects with name and slug
 */
export function generateWithSlug(items = {}, obj = null, fieldName = '') {
  return reduce(
    items,
    (result, value, key) => ({
      ...result,
      ...generateBaseSlug(key, obj, fieldName),
    }),
    {}
  );
}

/**
 * Gets calculated offer from passed deal data.
 * @param {Object} dealData
 * @returns {Object}
 */
function getCalculatedOfferFromDeal(dealData) {
  const {
    salesTaxAmount,
    securityDeposit,
    numberOfMonths: term,
    mileage: annualMileage,
    downPayment,
    monthlyPayment,
    fees,
    dueAtSigning,
  } = dealData;

  const sumOfNonCapitalizedFees = fees.find(({ name }) => name === NON_CAPITALIZED_TAXES_AND_FEES_SUM) || 0;

  return {
    annualMileage,
    downPayment,
    dueAtSigning,
    monthlyPayment,
    paymentWithTaxes: {
      securityDeposit,
      salesTaxAmount,
      sumOfNonCapitalizedFees,
      sumOfNonCapitalizedTaxes: 0,
    },
    term,
  };
}

/**
 * Returns correct state of digital retail.
 * @param {Object} context
 * @param {string} vin
 * @returns {Promise<*|boolean>}
 */
async function isPurchaseState(context, vin) {
  const location = await context.resolveValue('location', PageModel);
  const vehicle = await context.resolveValue(PATHS.VEHICLE({ vin }), InventoryModel);
  const { type } = vehicle;
  return getDigitalRetailPageState({ ...location, query: null }, isUsed(type), vehicle).isPurchase;
}

async function getOfferWithIncentives(offer, vin, context) {
  const isPurchaseEnable = get(offer, 'isPurchase');
  const isPurchase = isPurchaseEnable !== undefined ? isPurchaseEnable : await isPurchaseState(context, vin);
  if (offer && isPurchase) {
    const incentives = CarBuyingStorage.getValueFromSessionStorage(`${vin}.${STORAGE_KEYS.INCENTIVES}`);
    return incentives && incentives.loan ? { ...offer, incentives: incentives.loan } : offer;
  }

  return offer;
}

function getSortedIncentives(incentives, isPrimary = false) {
  return orderBy(
    incentives.filter(incentive => (isPrimary ? !incentive.conditionCategory : incentive.conditionCategory)),
    ['cash'],
    ['desc']
  );
}

function getSortedData(data) {
  const incentives = get(data, 'incentives') || [];
  const sortedPrimaryIncentives = getSortedIncentives(incentives, true);
  const sortedConditionalIncentives = getSortedIncentives(incentives);

  return { ...data, incentives: [...sortedPrimaryIncentives, ...sortedConditionalIncentives] };
}

export const CarBuyingModel = createModelSegment('carBuying', [
  {
    path: MODEL.VIN,
    async resolve({ vin }) {
      const defaultValue = {};

      if (isNode()) {
        return Promise.resolve(defaultValue);
      }

      return Promise.resolve(CarBuyingStorage.getValueFromLocalStorage(vin) || defaultValue);
    },
  },
  {
    path: MODEL.PROFILE,
    resolve() {
      const defaultValue = undefined;

      if (isNode()) {
        return Promise.resolve(defaultValue);
      }

      return Promise.resolve(CarBuyingStorage.getValueFromSessionStorage(STORAGE_KEYS.PROFILE));
    },
    update(data) {
      if (isNode()) {
        return Promise.resolve(undefined);
      }

      CarBuyingStorage.setValueToSessionStorage(STORAGE_KEYS.PROFILE, data);
      return Promise.resolve(data);
    },
  },
  {
    path: MODEL.DIGITAL_RETAIL_OFFERS,
  },
  {
    path: MODEL.CALCULATED_OFFER,
    async resolve({ vin }, context) {
      if (isNode()) {
        return Promise.resolve(undefined);
      }

      const offer = CarBuyingStorage.getValueFromLocalStorage(`${vin}.${STORAGE_KEYS.CALCULATED_OFFER}`) || null;

      return getOfferWithIncentives(offer, vin, context);
    },
    async update(data, { vin }, context) {
      if (isNode()) {
        return Promise.resolve(undefined);
      }

      const sortedData = getSortedData(data);
      const updatedData = { ...sortedData, updatedDate: Date.now() };

      CarBuyingStorage.setValueToLocalStorage(`${vin}.${STORAGE_KEYS.CALCULATED_OFFER}`, updatedData);
      return getOfferWithIncentives(updatedData, vin, context);
    },
  },
  {
    path: MODEL.DISCLAIMERS,
    async resolve({ vin }, context) {
      const defaultValue = '';

      try {
        let calculatedOffer = await context.resolveValue(`vins["${vin}"].${STORAGE_KEYS.CALCULATED_OFFER}`);
        const isPurchase = await isPurchaseState(context, vin);

        if (isPurchase) {
          return '';
        }

        if (!calculatedOffer) {
          const edmundsDeals = await context.resolveValue('data.idm.edmundsDeals', UserProfileModel, false);
          const dealData = edmundsDeals.find(({ vin: currentVin }) => currentVin === vin);
          if (!dealData) return defaultValue;
          calculatedOffer = getCalculatedOfferFromDeal(dealData);
        }

        const dynamicDisclaimerUrl = getDisclaimerUrl(vin, calculatedOffer);
        const { disclaimerText } = await withMetrics(EdmundsAPI, context).fetchJson(dynamicDisclaimerUrl);
        return disclaimerText;
      } catch {
        return defaultValue;
      }
    },
  },
  {
    path: MODEL.TOTAL_MONTHLY_PAYMENT,
    resolve({ vin }) {
      const defaultValue = null;

      if (isNode()) {
        return Promise.resolve(defaultValue);
      }

      return Promise.resolve(
        CarBuyingStorage.getValueFromLocalStorage(`${vin}.${STORAGE_KEYS.TOTAL_MONTHLY_PAYMENT}`) || defaultValue
      );
    },
    update(data, { vin }) {
      if (isNode()) {
        return Promise.resolve(null);
      }

      CarBuyingStorage.setValueToLocalStorage(`${vin}.${STORAGE_KEYS.TOTAL_MONTHLY_PAYMENT}`, data);
      return Promise.resolve(data);
    },
  },
  {
    path: MODEL.LAST_VISITED_STEP,
    async resolve({ vin }) {
      if (isNode()) {
        return Promise.resolve(undefined);
      }

      return CarBuyingStorage.getValueFromLocalStorage(`${vin}.${STORAGE_KEYS.LAST_VISITED_STEP}`) || null;
    },
    async update(data, { vin }) {
      if (isNode()) {
        return Promise.resolve(undefined);
      }

      CarBuyingStorage.setValueToLocalStorage(`${vin}.${STORAGE_KEYS.LAST_VISITED_STEP}`, data);
      return Promise.resolve(data);
    },
  },
  {
    path: MODEL.VISITED_TAB,
    resolve({ vin }) {
      const visitedTab = CarBuyingStorage.getValueFromLocalStorage(`${vin}.${STORAGE_KEYS.VISITED_TAB}`);
      return Promise.resolve(visitedTab);
    },
    update(visitedTab, { vin }) {
      if (isNode()) {
        return Promise.resolve(null);
      }

      CarBuyingStorage.setValueToLocalStorage(`${vin}.${STORAGE_KEYS.VISITED_TAB}`, visitedTab);
      return Promise.resolve(visitedTab);
    },
  },
  {
    path: MODEL.CURATED_MODELS,
    async resolve({ makeSlug, paymentType }, context) {
      try {
        const [{ zipCode }, options] = await Promise.all([
          context.resolveValue('location', VisitorModel),
          getDefaultApiOptionsFromContext(context),
        ]);
        const url = `/car-buying/v1/makes/${makeSlug}/models/${paymentType}-eligible?zip=${zipCode}`;
        const { models } = await withMetrics(EdmundsAPI, context).fetchJson(url, options);
        return models;
      } catch {
        return [];
      }
    },
  },
  {
    path: MODEL.CURATED_MAKES,
    async resolve({ paymentType }, context) {
      try {
        const [{ zipCode }, options] = await Promise.all([
          context.resolveValue('location', VisitorModel),
          getDefaultApiOptionsFromContext(context),
        ]);
        const url = `/car-buying/v1/makes/${paymentType}-eligible?zip=${zipCode}`;
        const { makes } = await withMetrics(EdmundsAPI, context).fetchJson(url, options);
        return makes;
      } catch {
        return undefined;
      }
    },
  },
  {
    path: MODEL.OFFERS_TOTAL_NUMBER,
    async resolve(match, context) {
      const { paymentType } = match;
      const makes = await context.resolveValue(PATHS.CURATED_MAKES({ paymentType }), CarBuyingModel);
      return makes && makes.reduce((acc, curr) => acc + curr.inventoryCount, 0);
    },
  },
  {
    path: MODEL.DEALER_MAPPING,
    async resolve({ vin, id }, context) {
      try {
        const franchiseId =
          id === EMPTY_PATH_VALUE
            ? await context.resolveValue(`vin["${vin}"].dealerInfo.franchiseId`, InventoryModel)
            : id;
        const url = `/dealer/v5/franchise/${franchiseId}`;
        const dealerInfo = await withMetrics(EdmundsAPI, context).fetchJson(url);
        const { id: provideId, provider } = get(dealerInfo, 'productFeatures.digitalRetail.credit', {});
        const routeOneDealerId = get(dealerInfo, 'productFeatures.digitalRetail.payment.id');
        const addOns = get(dealerInfo, 'productFeatures.digitalRetail.lease.addOns');
        return {
          mdocaId: provider === ROUTE_ONE_PROVIDER ? provideId : null,
          routeOneDealerId,
          addOns,
        };
      } catch {
        return {};
      }
    },
  },
  {
    path: MODEL.RECEIPT_ERROR,
  },
  {
    path: MODEL.PHOTO_URL,
    async resolve({ vin, ratio, disableML }, context) {
      const vehicle = await context.resolveValue(`vin["${vin}"]`, InventoryModel);
      const { photoUrls } = getPhotoConfig(vehicle, DEFAULT_WIDTH, get(ASPECT, ratio, null), disableML === 'true');
      return photoUrls[0];
    },
  },
  {
    path: MODEL.HIDE_INTERSTITIAL,
    async resolve(match, context) {
      const hideInterstitial = await context.resolveValue(HIDE_INTERSTITIAL, FeatureFlagModel);
      if (isNode()) {
        return hideInterstitial || Promise.resolve(undefined);
      }

      return (
        hideInterstitial ||
        Promise.resolve(CarBuyingStorage.getValueFromLocalStorage(STORAGE_KEYS.HIDE_INTERSTITIAL) || false)
      );
    },
    update(data) {
      CarBuyingStorage.setValueToLocalStorage(STORAGE_KEYS.HIDE_INTERSTITIAL, data);
      return Promise.resolve(data);
    },
  },
  {
    path: MODEL.HIDE_INTERSTITIAL_PER_SESSION,
    async resolve({ vin }) {
      if (isNode()) {
        return Promise.resolve(undefined);
      }

      return Promise.resolve(
        CarBuyingStorage.getValueFromSessionStorage(`${vin}.${STORAGE_KEYS.HIDE_INTERSTITIAL_PER_SESSION}`) || false
      );
    },
    update({ vin, value }) {
      CarBuyingStorage.setValueToSessionStorage(`${vin}.${STORAGE_KEYS.HIDE_INTERSTITIAL_PER_SESSION}`, value);
      return Promise.resolve(value);
    },
  },
  {
    path: MODEL.DELIGHTED_SURVEY_COMPLETED,
    async resolve() {
      if (isNode()) {
        return Promise.resolve(undefined);
      }

      return Promise.resolve(
        CarBuyingStorage.getValueFromLocalStorage(STORAGE_KEYS.DELIGHTED_SURVEY_COMPLETED) || false
      );
    },
    update(value) {
      CarBuyingStorage.setValueToLocalStorage(STORAGE_KEYS.DELIGHTED_SURVEY_COMPLETED, value);
      return Promise.resolve(value);
    },
  },
  {
    path: MODEL.SELECTED_LOAN_INCENTIVES,
    async resolve({ vin }) {
      const defaultValue = undefined;

      if (isNode()) {
        return Promise.resolve(defaultValue);
      }

      return Promise.resolve(CarBuyingStorage.getValueFromSessionStorage(`${vin}.${STORAGE_KEYS.INCENTIVES}.loan`));
    },
    update({ vin, incentives }) {
      const mappedLoanIncentives = incentives.map(
        ({ rebateAmount, amount, endDate, expireDate, name, id, ...details }) => ({
          cash: rebateAmount || amount,
          expirationDate: endDate || expireDate, // endDate and expireDate are the expiration Date of the conditional and primary loan incentives respectively
          programName: INCENTIVE_NAMES[name] || name,
          incentiveId: id,
          id,
          ...details,
        })
      );

      CarBuyingStorage.setValueToSessionStorage(`${vin}.${STORAGE_KEYS.INCENTIVES}.loan`, mappedLoanIncentives);

      return Promise.resolve(mappedLoanIncentives);
    },
  },
  {
    path: MODEL.SELECTED_LEASE_INCENTIVES,
    async resolve({ vin }) {
      const defaultValue = undefined;

      if (isNode()) {
        return Promise.resolve(defaultValue);
      }

      return Promise.resolve(CarBuyingStorage.getValueFromSessionStorage(`${vin}.${STORAGE_KEYS.INCENTIVES}.lease`));
    },
    update({ vin, incentives }) {
      CarBuyingStorage.setValueToSessionStorage(`${vin}.${STORAGE_KEYS.INCENTIVES}.lease`, incentives);
      return Promise.resolve(incentives);
    },
  },
  {
    path: MODEL.SELECTED_PRIMARY_LEASE_INCENTIVES,
    async resolve() {
      const defaultValue = undefined;

      if (isNode()) {
        return Promise.resolve(defaultValue);
      }

      return Promise.resolve([]);
    },
    update({ vin, incentives }) {
      CarBuyingStorage.setValueToSessionStorage(`${vin}.${STORAGE_KEYS.INCENTIVES}.primaryLease`, incentives);
      return Promise.resolve(incentives);
    },
  },
  {
    path: MODEL.SELECTED_PRIMARY_LOAN_INCENTIVES,
    async resolve() {
      const defaultValue = undefined;

      if (isNode()) {
        return Promise.resolve(defaultValue);
      }

      return Promise.resolve([]);
    },
    update({ vin, incentives }) {
      const mappedLoanIncentives = incentives.map(
        ({ rebateAmount, amount, endDate, expireDate, name, id, restrictions, ...details }) => ({
          cash: rebateAmount || amount,
          expirationDate: endDate || expireDate,
          programName: INCENTIVE_NAMES[name] || name,
          incentiveId: id,
          programDescription: restrictions,
          id,
          ...details,
        })
      );

      CarBuyingStorage.setValueToSessionStorage(`${vin}.${STORAGE_KEYS.INCENTIVES}.primaryLoan`, mappedLoanIncentives);
      return Promise.resolve(mappedLoanIncentives);
    },
  },
  {
    path: MODEL.SELECTED_INCENTIVES,
    resolve({ vin }) {
      const defaultValue = null;

      if (isNode()) {
        return Promise.resolve(defaultValue);
      }

      return Promise.resolve(
        CarBuyingStorage.getValueFromSessionStorage(`${vin}.${STORAGE_KEYS.INCENTIVES}`) || defaultValue
      );
    },
    update(data, { vin }) {
      if (isNode()) {
        return Promise.resolve(null);
      }

      CarBuyingStorage.setValueToSessionStorage(`${vin}.${STORAGE_KEYS.INCENTIVES}`, data);
      return Promise.resolve(data);
    },
  },
  {
    path: MODEL.CONDITIONAL_LEASE_INCENTIVES,
    async resolve() {
      const defaultValue = undefined;

      if (isNode()) {
        return Promise.resolve(defaultValue);
      }

      return Promise.resolve([]);
    },
    update({ vin, incentives }) {
      CarBuyingStorage.setValueToSessionStorage(`${vin}.${STORAGE_KEYS.INCENTIVES}.conditionalLease.all`, incentives);
      return Promise.resolve(incentives);
    },
  },
  {
    path: MODEL.CONDITIONAL_LEASE_INCENTIVES_ELIGIBLE,
    async resolve() {
      const defaultValue = undefined;

      if (isNode()) {
        return Promise.resolve(defaultValue);
      }

      return Promise.resolve([]);
    },
    update({ vin, incentives }) {
      CarBuyingStorage.setValueToSessionStorage(
        `${vin}.${STORAGE_KEYS.INCENTIVES}.conditionalLease.eligible`,
        incentives
      );
      return Promise.resolve(incentives);
    },
  },
  {
    path: MODEL.CONDITIONAL_LOAN_INCENTIVES,
    async resolve() {
      const defaultValue = undefined;

      if (isNode()) {
        return Promise.resolve(defaultValue);
      }

      return Promise.resolve([]);
    },
    update({ vin, incentives }) {
      const mappedLoanIncentives = incentives.map(
        ({ rebateAmount, amount, endDate, expireDate, name, id, restrictions, ...details }) => ({
          cash: rebateAmount || amount,
          expirationDate: endDate || expireDate,
          programName: name,
          incentiveId: id,
          programDescription: restrictions,
          id,
          ...details,
        })
      );

      CarBuyingStorage.setValueToSessionStorage(
        `${vin}.${STORAGE_KEYS.INCENTIVES}.conditionalLoan.all`,
        mappedLoanIncentives
      );
      return Promise.resolve(mappedLoanIncentives);
    },
  },
  {
    path: MODEL.IS_OVERLAPPING_DEALER,
    async resolve({ vin }, context) {
      try {
        const rooftopId = await context.resolveValue(`vin["${vin}"].dealerInfo.rooftopId`, InventoryModel);
        const options = await getDefaultApiOptionsFromContext(context);
        const url = `/car-buying/v1/participating-dealers/${rooftopId}`;
        const overLappingInfo = await withMetrics(EdmundsAPI, context).fetchJson(url, options);
        return get(overLappingInfo, 'participating', false);
      } catch {
        return false;
      }
    },
  },
  {
    path: MODEL.RESET_CUSTOM_VEHICLE_PRICE,
    async resolve() {
      if (isNode()) {
        return Promise.resolve(undefined);
      }

      return Promise.resolve(false);
    },

    update(data) {
      if (isNode()) {
        return Promise.resolve(undefined);
      }

      return Promise.resolve(data);
    },
  },
  {
    path: MODEL.STYLE_LEASE_TERMS,
    async resolve({ styleId }, context) {
      try {
        const { zipCode } = await context.resolveValue('location', VisitorModel);
        const url = `/car-buying/v2/lease-terms/?styleid=${styleId}&zip=${zipCode}`;
        const leaseTerms = await withMetrics(EdmundsAPI, context).fetchJson(url);
        return Object.entries(get(leaseTerms, 'terms', {})).reduce((acc, [annualMileage, values]) => {
          acc[annualMileage] = values.map(({ residualValue, termInMonths, ...otherValues }) => ({
            annualMileage: parseInt(annualMileage, 10),
            residual: residualValue,
            term: termInMonths,
            securityDeposit: 0,
            ...otherValues,
          }));
          return acc;
        }, {});
      } catch {
        return null;
      }
    },
  },
]);
